import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Search, Kanban, MessagesSquare, FileSignature, Wallet, CalendarRange, BellRing, Globe2 } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Reveal, SectionHead, EASE } from "@/components/Motion";
import { EventCard } from "@/components/EventCard";
import EventDialog from "@/components/EventDialog";
import { useAuth } from "@/context/AuthContext";
import WinnersSpotlight from "@/components/WinnersSpotlight";
import { HeroWorldMap } from "@/components/HeroWorldMap";
import { CollabAds } from "@/components/CollabAds";
import { api, fmtDate } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const TYPES = [["Brands", "Brand"], ["Sponsors", "Sponsor"], ["Events", "Event"], ["Creators", "Creator"], ["Agencies", "Agency"], ["Publishers", "Publisher"], ["Venues", "Venue"], ["Sports Teams", "Sports Team"], ["Media Companies", "Media Company"], ["Nonprofits", "Nonprofit"], ["Investors", "Investor"], ["Hospitality Groups", "Hospitality Group"]];

function LiveStats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/stats/public").then((r) => setS(r.data)).catch(() => {}); }, []);
  const items = [
    [s?.companies ?? "–", "companies listed"],
    [s?.opportunities ?? "–", "open opportunities"],
    [s?.events ?? "–", "upcoming events"],
  ];
  if (s?.deals_this_month) items.push([s.deals_this_month, "deals started this month"]);
  return (
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8 mt-12">
      <p className="text-sm text-slate-300" data-testid="hero-stats-note">A growing network of brands, creators and events. These are live counts of real members, not including sample profiles.</p>
      <div className={`mt-4 grid grid-cols-2 ${items.length > 3 ? "lg:grid-cols-4" : "sm:grid-cols-3"} border-t border-[#2A1240]/10`} data-testid="hero-stats">
        {items.map(([v, l], i) => (
          <div key={l} className={`py-6 pr-4 ${i ? "sm:pl-6 sm:border-l border-[#2A1240]/10" : ""}`}>
            <p className="font-display text-3xl sm:text-4xl text-[#2A1240]" data-testid={`stat-value-${i}`}>{v}</p>
            <p className="mt-1 text-sm text-slate-400">{l}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const STEPS = [
  ["Create your profile", "Sign up free with any email and tell partners who you are, where you work and what you're looking for."],
  ["Post or discover", "Post an event, brief or opportunity, or search companies by country, industry and budget."],
  ["Talk and agree", "Message partners, send proposals, book meetings and sign the contract online."],
  ["Get paid and track", "Send invoices, take card, crypto or cash payments and track the deal to renewal."],
];

export const HowItWorks = () => (
  <section className="py-14 sm:py-16 bg-white border-y border-[#2A1240]/10" data-testid="how-it-works">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <p className="kicker">How it works</p>
      <ol className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map(([t, d], i) => (
          <li key={t} className="rounded-2xl border border-[#2A1240]/10 bg-[#FFF7FB] p-6" data-testid={`how-step-${i + 1}`}>
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full btn-gold !p-0 text-sm">{i + 1}</span>
            <h3 className="mt-4 text-lg font-semibold text-[#2A1240]">{t}</h3>
            <p className="mt-2 text-sm text-slate-300">{d}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);
const STAGES = ["Prospect", "Contacted", "Meeting", "Proposal", "Negotiation", "Contract", "Won"];

const HeroVisual = () => (
  <motion.figure initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.3 }}
    className="relative aspect-[4/5] sm:aspect-[5/6] overflow-hidden rounded-2xl border border-[#2A1240]/10" data-testid="hero-visual">
    <img src="/img/hero.jpg" alt="Audience at an industry conference" className="absolute inset-0 h-full w-full object-cover" />
    <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#FFF7FB]/90 to-transparent p-5 pt-16 text-sm text-slate-200">
      Conferences, festivals and launches post their sponsorship packages here.
    </figcaption>
  </motion.figure>
);

function Hero() {
  const { user } = useAuth();
  const nav = useNavigate();
  return (
    <section className="relative pt-28 sm:pt-36 pb-16 overflow-hidden" data-testid="hero-section">
      <HeroWorldMap />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <motion.p className="kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
            For marketing and partnerships teams worldwide
          </motion.p>
          <h1 className="sr-only">Where Brands Meet Their Next Big Opportunity</h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.9, ease: EASE }}
            className="mt-6 max-w-xl font-display text-2xl sm:text-3xl lg:text-4xl leading-snug text-[#2A1240]" data-testid="hero-subheadline">
            Discover, connect, negotiate, and manage partnerships, all in one platform.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.9, ease: EASE }}
            className="mt-10 flex flex-col sm:flex-row gap-3">
            <button className="btn-gold !px-8 !py-4" onClick={() => nav("/discover")} data-testid="hero-find-partner-button">
              Find a Partner <ArrowRight className="h-4 w-4" />
            </button>
            <button className="btn-ghost !px-8 !py-4" onClick={() => nav(user ? "/app/network?new=1" : "/register?next=/app/network?new=1")} data-testid="hero-post-opportunity-button">
              Post an Opportunity
            </button>
          </motion.div>
          <p className="mt-6 flex items-start gap-2 max-w-xl text-sm text-slate-300" data-testid="hero-international-note">
            <Globe2 className="h-4 w-4 mt-0.5 shrink-0 text-[#E0218A]" />
            Balqis Book is international. Companies from every country can post their events, brands and sponsorships.
          </p>
        </div>
        <div className="lg:col-span-5 px-4 sm:px-8 lg:px-0">
          <HeroVisual />
        </div>
      </div>
      <LiveStats />
    </section>
  );
}

const Marquee = () => (
  <section className="py-8 bg-[#FFF7FB]" data-testid="editorial-marquee">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <p className="kicker">Who's on Balqis Book</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {TYPES.map(([label, type]) => (
          <Link key={type} to={`/discover?type=${encodeURIComponent(type)}`} className="rounded-full border border-[#2A1240]/15 bg-white px-4 py-2 text-sm text-[#2A1240] hover:border-[#E0218A] hover:text-[#E0218A]" data-testid={`category-chip-${type.replace(/\s/g, "-")}`}>{label}</Link>
        ))}
      </div>
    </div>
  </section>
);

function LiveEvents() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/events", { params: { when: "upcoming" } })
      .then((r) => setEvents(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEvents([]));
  }, []);
  if (!events.length) return null;
  const [first, ...rest] = events;
  return (
    <section className="py-16 sm:py-20" data-testid="live-events-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <SectionHead kicker="Upcoming events" title="Events looking for partners"
            sub="Posted by companies on Balqis Book. Anyone can browse; only signed-in teams can post." />
          <Link to="/events" className="btn-ghost self-start md:self-auto" data-testid="home-all-events-link">All events <ArrowUpRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-12">
          <div className="md:col-span-7"><EventCard e={first} onOpen={setOpen} tall /></div>
          <div className="md:col-span-5 grid gap-6">
            {rest.slice(0, 2).map((e, i) => <EventCard key={e.id} e={e} onOpen={setOpen} i={i + 1} />)}
          </div>
        </div>
      </div>
      <EventDialog event={open} onClose={() => setOpen(null)} />
    </section>
  );
}

const Tile = ({ className = "", icon: Icon, kicker, title, children, testid, delay = 0 }) => (
  <Reveal delay={delay} className={`group relative rounded-3xl border border-[#2A1240]/10 bg-[#FFFFFF] p-7 sm:p-8 overflow-hidden hover:border-[#E0218A]/40 transition-colors ${className}`}>
    <div data-testid={testid} className="h-full flex flex-col">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-full border border-[#E0218A]/40 text-[#E0218A] group-hover:bg-[#E0218A] group-hover:text-white transition-colors"><Icon className="h-4 w-4" /></span>
        <p className="kicker">{kicker}</p>
      </div>
      <h3 className="mt-6 font-display text-2xl sm:text-3xl text-[#2A1240] leading-tight">{title}</h3>
      <div className="mt-auto pt-6">{children}</div>
    </div>
  </Reveal>
);

const Bento = () => (
  <section className="py-16 sm:py-20 bg-[#FFF0F7]" data-testid="platform-bento">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead kicker="What you can do" title="From the first message to the final invoice" />
      <div className="mt-14 grid gap-5 md:grid-cols-6 lg:grid-cols-12 auto-rows-auto">
        <Reveal className="md:col-span-6 lg:col-span-7 lg:row-span-2 group relative rounded-3xl border border-[#2A1240]/10 overflow-hidden hover:border-[#E0218A]/40 transition-colors">
          <div className="relative h-full min-h-[380px]" data-testid="bento-discover">
            <img src="/img/roundtable.jpg" alt="A team meeting around a table with laptops" className="absolute inset-0 h-full w-full object-cover opacity-60" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF] via-[#FFFFFF]/70 to-[#FFFFFF]/10" />
            <div className="relative p-8 flex flex-col justify-between h-full min-h-[380px]">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full border border-[#E0218A]/40 text-[#E0218A] bg-[#FFF7FB]/60"><Search className="h-4 w-4" /></span>
                <p className="kicker">Discover</p>
              </div>
              <div>
                <h3 className="font-display text-3xl sm:text-4xl text-[#2A1240] max-w-md">Search companies by industry, location and budget.</h3>
                <div className="mt-6 flex flex-wrap gap-2">
                  {["Fashion", "Luxury", "Sports", "Middle East", "$100K–$500K"].map((t) => <span key={t} className="rounded-full border border-[#2A1240]/15 bg-[#FFF7FB]/40 px-3 py-1 text-xs text-slate-200">{t}</span>)}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
        <Tile className="md:col-span-6 lg:col-span-5" icon={Kanban} kicker="Pipeline" title="Track every deal in one pipeline." testid="bento-pipeline" delay={0.05}>
          <div className="flex flex-wrap gap-1.5">
            {STAGES.map((s, i) => (
              <span key={s} className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${i === 6 ? "bg-emerald-400 text-white" : "bg-[#2A1240]/5 text-slate-200 border border-[#2A1240]/10"}`}>{s}</span>
            ))}
          </div>
        </Tile>
        <Tile className="md:col-span-3 lg:col-span-5" icon={MessagesSquare} kicker="Messaging" title="Message partners, send proposals, book meetings." testid="bento-messaging" delay={0.1}>
          <p className="text-sm text-slate-400">Files and contract links can be shared right in the conversation.</p>
        </Tile>
        <Tile className="md:col-span-3 lg:col-span-4" icon={FileSignature} kicker="Agreements" title="Send contracts and collect signatures." testid="bento-contracts">
          <p className="text-sm text-slate-400">See who has signed, store documents and get a reminder before a contract runs out.</p>
        </Tile>
        <Tile className="md:col-span-3 lg:col-span-4" icon={Wallet} kicker="Payments" title="Send invoices and get paid." testid="bento-payments" delay={0.05}>
          <p className="text-sm text-slate-400">Card, crypto or cash. Receipts go out automatically.</p>
        </Tile>
        <Tile className="md:col-span-3 lg:col-span-4" icon={CalendarRange} kicker="Calendar" title="Share a calendar with partners." testid="bento-calendar" delay={0.1}>
          <p className="text-sm text-slate-400">Meetings, campaign dates, deliverables and renewals in one view.</p>
        </Tile>
        <Tile className="md:col-span-6 lg:col-span-12" icon={BellRing} kicker="Alerts" title="Get an alert when a new opportunity fits your profile." testid="bento-alerts">
          <div className="flex flex-wrap gap-3 text-sm text-slate-300">
            <span className="flex items-center gap-2"><BellRing className="h-4 w-4 text-[#E0218A]" />Renewal reminders</span>
            <span className="flex items-center gap-2"><FileSignature className="h-4 w-4 text-[#E0218A]" />Signature updates</span>
          </div>
        </Tile>
      </div>
    </div>
  </section>
);

function Opportunities() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/opportunities").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);
  if (!items.length) return null;
  return (
    <section className="py-16 sm:py-20" data-testid="home-opportunities">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <SectionHead kicker="Open now" title="Open opportunities" sub="Brands, sponsors and events currently looking for a partner." />
        </div>
        <div className="lg:col-span-8 divide-y divide-[#2A1240]/10 border-y border-[#2A1240]/10">
          {items.slice(0, 5).map((o, i) => (
            <Reveal key={o.id} delay={i * 0.05}>
              <Link to={`/companies/${o.company_id}`} className="group flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-6" data-testid={`home-opportunity-${o.id}`}>
                <div>
                  <p className="text-xs text-slate-400">{o.company_name} · {o.industry}</p>
                  <p className="mt-1 font-display text-xl sm:text-2xl text-[#2A1240] group-hover:text-[#8E2BC4] transition-colors">{o.title}</p>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="font-mono text-[#8E2BC4]">{o.budget}</span>
                  {o.deadline && <span className="text-slate-400">by {fmtDate(o.deadline)}</span>}
                  <ArrowUpRight className="h-5 w-5 text-slate-400 group-hover:text-[#E0218A] transition-colors" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const CTA = () => (
  <section className="pb-16 sm:pb-20" data-testid="home-cta">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="relative overflow-hidden rounded-2xl border border-[#2A1240]/10">
        <img src="/img/handshake.jpg" alt="Two partners shaking hands" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFF7FB] via-[#FFF7FB]/85 to-[#FFF7FB]/30" />
        <div className="relative p-8 sm:p-16 max-w-2xl">
          <p className="kicker">Free to join</p>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl text-[#2A1240] leading-[1.05]">Ready to find a partner?</h2>
          <p className="mt-5 text-slate-300">Sign up free with any email address and start browsing companies, events and opportunities right away.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/register" className="btn-gold !px-8 !py-4" data-testid="cta-join-button">Create your company profile <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/discover" className="btn-ghost !px-8 !py-4" data-testid="cta-discover-button">Browse partners</Link>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  useSeo("Where Brands Meet Their Next Big Opportunity", "Balqis Book is the partnership platform for marketing teams: discover verified brands, sponsors and events, then negotiate, sign and get paid in one place.", "/");
  return (
    <div className="bg-[#FFF7FB] text-[#2A1240]" data-testid="home-page">
      <Nav />
      <Hero />
      <CollabAds />
      <WinnersSpotlight />
      <Marquee />
      <HowItWorks />
      <LiveEvents />
      <Bento />
      <Opportunities />
      <CTA />
      <Footer />
    </div>
  );
}
